// Zdrojové cenníky dodávateľov a % úprava cien — sekcie Nastavení
// (presunuté z pages/NastaveniaPage.tsx v KOLE 170, správanie bez zmeny).
import { useRef, useState } from "react";
import { usePrava } from "@/api/hooks/useAuth";
import { VETA, smieCennik } from "@/lib/prava";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { ImportNahladDialog } from "@/components/nastavenia/ImportNahladDialog";
import {
  useNahladZdroja,
  useUploadZdroj,
  useUpravaCienDodavatela,
  useUpravaCienHistoria,
  useUpravaCienSkupiny,
  useUpravaCienStav,
  useZdrojeCennikov,
  useZrusUpravuCien,
  type UpravaCienSkupina,
  type ZdrojCennika,
  type ZdrojNahlad,
} from "@/api/hooks/useCennik";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** Formátovanie veľkosti súboru (B → kB/MB). */
function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDatum(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("sk-SK", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });
}

/**
 * Zdrojové cenníky dodávateľov — prehľad + upload nových verzií.
 *
 * Cenník s aktívnou % úpravou cien (KOLO 165, per cenník od KOLA 168):
 * import ju uplatní aj na nový súbor, takže pred výberom súboru okno povie
 * o úprave a ponúkne ju zrušiť — keď nový cenník zdraženie už obsahuje,
 * stúpli by ceny druhýkrát. Zruší sa len úprava tohto cenníka; ostatné
 * cenníky dodávateľa si svoju nechajú.
 *
 * Nahratie ide cez náhľad (KOLO 165): server spustí import naprázdno
 * a okno ukáže, čo pribudne, čomu sa zmení cena a čo zmizne. Zapíše sa
 * až „Potvrdiť import" — ten istý súbor ešte raz, s odtlačkom zmiznutých
 * položiek, aby server vyradil len to, čo user v náhľade videl.
 */
export function DodavateliaSection(): JSX.Element {
  // Cenník CRC (tovar) nahráva obchodník, ostatné rozpočtár (KOLO 231)
  const prava = usePrava();
  const zdrojeQuery = useZdrojeCennikov();
  const nahladMutation = useNahladZdroja();
  const uploadMutation = useUploadZdroj();
  const zdroje = zdrojeQuery.data?.data ?? [];
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadSource, setUploadSource] = useState<ZdrojCennika | null>(null);
  const [zrusitUpravu, setZrusitUpravu] = useState(false);
  /** Zdroj, pri ktorom sa pred výberom súboru pýtame na % úpravu. */
  const [sUpravou, setSUpravou] = useState<ZdrojCennika | null>(null);
  /** Náhľad čaká na potvrdenie — súbor ostáva v pamäti na druhé odoslanie. */
  const [nahlad, setNahlad] = useState<{
    z: ZdrojCennika;
    file: File;
    zrusit: boolean;
    data: ZdrojNahlad;
  } | null>(null);
  const zaneprazdneny = nahladMutation.isPending || uploadMutation.isPending;

  function vyberSubor(z: ZdrojCennika, zrusit: boolean): void {
    setUploadSource(z);
    setZrusitUpravu(zrusit);
    // accept sa mení podľa zdroja — nastav pred otvorením dialógu
    if (fileRef.current) {
      fileRef.current.accept = z.accept;
      fileRef.current.click();
    }
  }

  function startUpload(z: ZdrojCennika): void {
    if (z.uprava_percent != null) {
      setZrusitUpravu(false);
      setSUpravou(z);
      return;
    }
    vyberSubor(z, false);
  }

  /** Výber súboru → náhľad. Nič sa nezapíše, kým user nepotvrdí. */
  async function handleFile(file: File): Promise<void> {
    const z = uploadSource;
    if (!z) return;
    try {
      const data = await nahladMutation.mutateAsync({
        source: z.source,
        file,
        zrusitUpravu,
      });
      setNahlad({ z, file, zrusit: zrusitUpravu, data });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Náhľad cenníka zlyhal",
        description: extractErrorMessage(err),
      });
    } finally {
      setUploadSource(null);
      setZrusitUpravu(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function potvrdit(): Promise<void> {
    if (!nahlad) return;
    const { z, file, zrusit, data } = nahlad;
    try {
      const d = await uploadMutation.mutateAsync({
        source: z.source,
        file,
        zrusitUpravu: zrusit,
        kontrolaChybajucich: data.kontrola,
      });
      const zrusena =
        d.zrusena_uprava_percent != null
          ? ` Úprava cien ${formatPct(d.zrusena_uprava_percent)} % tohto cenníka zrušená.`
          : "";
      const zmiznute = [
        d.vyradenych ? `${d.vyradenych} vyradených` : "",
        d.oznacenych ? `${d.oznacenych} so štítkom „nie je v cenníku“` : "",
        d.obnovenych ? `${d.obnovenych} vrátených` : "",
      ].filter(Boolean);
      toast({
        title: `${z.label} — cenník aktualizovaný`,
        description: `${d.created} nových, ${d.updated} upravených položiek${
          zmiznute.length ? `, ${zmiznute.join(", ")}` : ""
        }${d.errors.length ? `, ${d.errors.length} chýb` : ""}.${zrusena}`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Import cenníka zlyhal",
        description: extractErrorMessage(err),
      });
    } finally {
      setNahlad(null);
    }
  }

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <Dialog
        open={sUpravou != null}
        onOpenChange={(o) => {
          if (!o) setSUpravou(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nahrať {sUpravou?.label}</DialogTitle>
            <DialogDescription>
              Pre tento cenník platí úprava cien{" "}
              <b>{formatPct(sUpravou?.uprava_percent ?? 0)} %</b> — uplatní sa
              aj na jeho novú verziu.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={zrusitUpravu}
              onChange={(e) => setZrusitUpravu(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-primary"
            />
            <span>
              Nový cenník už obsahuje zmenu — zrušiť úpravu tohto cenníka
              <span className="block text-xs text-muted-foreground">
                Zruší sa len pre tento cenník. Ostatné cenníky dodávateľa si
                svoju úpravu nechajú.
              </span>
            </span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSUpravou(null)}>
              Zrušiť
            </Button>
            <Button
              onClick={() => {
                const z = sUpravou;
                setSUpravou(null);
                if (z) vyberSubor(z, zrusitUpravu);
              }}
            >
              <Upload className="h-3.5 w-3.5" />
              Vybrať súbor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ImportNahladDialog
        open={nahlad != null}
        label={nahlad?.z.label ?? ""}
        nahlad={nahlad?.data ?? null}
        potvrdzuje={uploadMutation.isPending}
        onPotvrdit={() => void potvrdit()}
        onZrusit={() => setNahlad(null)}
      />
      <div>
        <h2 className="text-xl">Dodávatelia — zdrojové cenníky</h2>
        <p className="text-xs text-muted-foreground">
          Nahrané súbory, z ktorých sa importoval cenník. „Nahrať novú
          verziu" najprv ukáže náhľad — čo pribudne, čomu sa zmení cena a čo
          z cenníka zmizne; zapíše sa až po potvrdení. Veľký cenník (Häfele)
          sa počíta aj niekoľko minút.
        </p>
      </div>

      <input
        ref={fileRef}
        type="file"
        className="hidden"
        aria-label="Nová verzia cenníka"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
        }}
      />

      {zdrojeQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Načítavam…</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cenník</TableHead>
                <TableHead>Súbor</TableHead>
                <TableHead className="text-right">Položiek</TableHead>
                <TableHead className="text-right">Veľkosť</TableHead>
                <TableHead className="text-right">Aktualizovaný</TableHead>
                <TableHead className="w-[190px] text-right">Akcie</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {zdroje.map((z) => (
                <TableRow key={z.source}>
                  <TableCell>
                    <div className="text-sm font-medium">{z.label}</div>
                    <div className="text-xs text-muted-foreground">
                      {z.popis}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {z.filename}
                    {z.nahrany_manualne ? (
                      <span className="ml-1 rounded bg-primary/10 px-1 py-0.5 text-[10px] text-primary">
                        nahraný ručne
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {z.pocet_poloziek != null
                      ? z.pocet_poloziek.toLocaleString("sk-SK")
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatBytes(z.size_bytes)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatDatum(z.updated_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    {z.upload_enabled && !smieCennik(prava, z.je_tovar) ? (
                      <span
                        className="text-xs text-muted-foreground"
                        title={z.je_tovar ? VETA.cennikTovaru : VETA.cennikVyroby}
                      >
                        {z.je_tovar ? "nahráva obchodník" : "nahráva rozpočtár"}
                      </span>
                    ) : z.upload_enabled ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={zaneprazdneny}
                        onClick={() => startUpload(z)}
                        title="Nahrať nový súbor cenníka — najprv ukáže náhľad zmien"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        {nahladMutation.isPending &&
                        uploadSource?.source === z.source
                          ? "Počítam náhľad…"
                          : uploadMutation.isPending &&
                              nahlad?.z.source === z.source
                            ? "Importuje sa…"
                            : "Nahrať novú verziu"}
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        len cez repozitár
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

    </section>
  );
}

/** Kľúč cenníka v rozbaľovačke: dodávateľ a cenník (`""` = bez cenníka). */
function klucSkupiny(s: { dodavatel: string; zdroj?: string | null }): string {
  return `${s.dodavatel}|${s.zdroj ?? ""}`;
}

/**
 * Meno cenníka na zobrazenie — `JafHolz — Kronospan DTDL`, pri položkách
 * bez cenníka `JafHolz — ostatné`. Záznam histórie spred KOLA 168 cenník
 * nemá (úprava platila pre celého dodávateľa) — vtedy len dodávateľ.
 */
function nazovSkupiny(s: {
  dodavatel: string;
  dodavatel_nazov?: string | null;
  zdroj?: string | null;
  zdroj_nazov?: string | null;
}): string {
  const dodavatel = s.dodavatel_nazov || s.dodavatel;
  if (s.zdroj == null) return dodavatel;
  if (s.zdroj === "") return `${dodavatel} — ostatné`;
  return s.zdroj_nazov || `${dodavatel} — ${s.zdroj}`;
}

/**
 * Percentuálna úprava cien cenníka — samostatná sekcia s históriou.
 *
 * Od KOLA 168 patrí úprava cenníku, nie dodávateľovi: JafHolz má šesť
 * cenníkov a nový cenník jedného nesmie zdediť zdraženie iného. Výber
 * ponúka cenníky z položiek (`/uprava-cien-skupiny`) a k nim úpravy,
 * ktorých cenník položky už nemá — aj tie sa musia dať zrušiť. Sklo
 * backend neponúka (ceny skla mení len jeho cenník).
 */
export function UpravaCienSection(): JSX.Element {
  // Ceny tovaru upravuje obchodník, ostatné rozpočtár (KOLO 231)
  const prava = usePrava();
  const skupinyQuery = useUpravaCienSkupiny();
  const stavQuery = useUpravaCienStav();
  const upravaMutation = useUpravaCienDodavatela();
  const zrusMutation = useZrusUpravuCien();
  const [kluc, setKluc] = useState("");
  const [percent, setPercent] = useState("");

  const stav = stavQuery.data?.data ?? [];
  const skupiny: UpravaCienSkupina[] = [...(skupinyQuery.data?.data ?? [])];
  for (const s of stav) {
    if (!skupiny.some((x) => klucSkupiny(x) === klucSkupiny(s))) {
      skupiny.push({
        dodavatel: s.dodavatel,
        dodavatel_nazov: s.dodavatel_nazov ?? s.dodavatel,
        zdroj: s.zdroj,
        zdroj_nazov: s.zdroj_nazov ?? s.zdroj,
        pocet: 0,
        percent: s.percent,
        je_tovar: s.je_tovar ?? false,
      });
    }
  }
  const vybrana = skupiny.find((s) => klucSkupiny(s) === kluc) ?? null;
  const smieSkupinu = vybrana == null || smieCennik(prava, vybrana.je_tovar);
  const aplikovana = vybrana
    ? stav.find((s) => klucSkupiny(s) === klucSkupiny(vybrana))
    : undefined;
  const vybranyNazov = vybrana ? nazovSkupiny(vybrana) : "";
  // História celého dodávateľa — záznamy spred KOLA 168 cenník nemajú
  const historiaQuery = useUpravaCienHistoria(vybrana?.dodavatel);
  const historia = historiaQuery.data?.data ?? [];

  const percentNum = parseFloat(percent);
  const platny =
    !!vybrana &&
    vybrana.pocet > 0 &&
    Number.isFinite(percentNum) &&
    percentNum !== 0 &&
    percentNum >= -50 &&
    percentNum <= 100;

  async function handleUprava(): Promise<void> {
    if (!platny || !vybrana) return;
    const smer = percentNum > 0 ? "Zvýšiť" : "Znížiť";
    if (
      !window.confirm(
        `${smer} nákupné ceny cenníka „${vybranyNazov}" o ${Math.abs(
          percentNum,
        )} %? Zmena sa prejaví vo všetkých nových kalkuláciách.`,
      )
    ) {
      return;
    }
    try {
      const d = await upravaMutation.mutateAsync({
        dodavatel: vybrana.dodavatel,
        zdroj: vybrana.zdroj,
        percent,
      });
      toast({
        title: `Ceny „${vybranyNazov}“ upravené o ${formatPct(d.percent ?? percent)} %`,
        description: `${d.upravenych} položiek dostalo novú nákupnú cenu.`,
      });
      setPercent("");
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Úprava cien zlyhala",
        description: extractErrorMessage(err),
      });
    }
  }

  async function handleZrusit(): Promise<void> {
    if (!aplikovana || !vybrana) return;
    if (
      !window.confirm(
        `Zrušiť aplikovanú úpravu ${formatPct(aplikovana.percent)} % ` +
          `cenníka „${vybranyNazov}"?\n\nCeny sa vrátia inverzným ` +
          "prepočtom (približne — zaokrúhľuje sa na 4 des. miesta). " +
          "Ďalší import cenníka už dá ceny presne podľa súboru.",
      )
    ) {
      return;
    }
    try {
      const d = await zrusMutation.mutateAsync({
        dodavatel: vybrana.dodavatel,
        zdroj: vybrana.zdroj,
      });
      toast({
        title: `Úprava cien „${vybranyNazov}“ zrušená`,
        description: `${d.upravenych} položiek sa vrátilo na pôvodné ceny.`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Zrušenie úpravy zlyhalo",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div>
        <h2 className="text-xl">Percentuálna úprava cien cenníka</h2>
        <p className="text-xs text-muted-foreground">
          Dodávateľ zdražel a nový cenník ešte nie je? Posuň nákupné ceny jeho
          cenníka o ± %. Úpravu môžeš kedykoľvek doladiť ďalšou ± % zmenou
          (pozor, +10 % a potom −10 % nie je presný návrat). Úprava patrí
          cenníku — iný cenník toho istého dodávateľa ju nemá a nový cenník ju
          od iného nezdedí. „Ostatné“ sú položky dodávateľa bez cenníka (ručne
          pridané, z migrácií). Úprava platí, kým ju nezrušíš — nahratie
          cenníka ani deploy ju nezrušia, import ju uplatní na ceny zo súboru.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 space-y-1">
          <Label htmlFor="uc-cennik">Cenník</Label>
          <Select value={kluc} onValueChange={setKluc}>
            <SelectTrigger id="uc-cennik" className="w-[340px] max-w-full">
              <SelectValue placeholder="Vybrať…" />
            </SelectTrigger>
            <SelectContent>
              {skupiny.map((s) => (
                <SelectItem key={klucSkupiny(s)} value={klucSkupiny(s)}>
                  {nazovSkupiny(s)} ({s.pocet.toLocaleString("sk-SK")})
                  {s.percent != null ? ` · ${formatPct(s.percent)} %` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="uc-percent">Zmena (%)</Label>
          <Input
            id="uc-percent"
            type="number"
            min={-50}
            max={100}
            step="0.5"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            placeholder="+5"
            className="w-[110px]"
          />
        </div>
        <Button
          size="sm"
          disabled={!platny || upravaMutation.isPending || !smieSkupinu}
          onClick={() => void handleUprava()}
          title="Vynásobí nákupné ceny všetkých aktívnych položiek vybraného cenníka"
        >
          {upravaMutation.isPending ? "Upravuje sa…" : "Upraviť ceny"}
        </Button>
      </div>
      {aplikovana ? (
        <div className="flex flex-wrap items-center gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          <span>
            Aktuálne aplikovaná úprava pre „{vybranyNazov}":{" "}
            <b>{formatPct(aplikovana.percent)} %</b>
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-xs"
            disabled={zrusMutation.isPending || !smieSkupinu}
            onClick={() => void handleZrusit()}
            title="Vráti ceny cenníka inverzným prepočtom (približne); ďalší import cenníka dá ceny presne podľa súboru"
          >
            {zrusMutation.isPending ? "Ruší sa…" : "Zrušiť úpravu"}
          </Button>
        </div>
      ) : null}

      <div className="space-y-1">
        <h3 className="text-sm font-medium">
          História úprav{vybrana ? ` — ${vybrana.dodavatel_nazov}` : ""}
        </h3>
        {historia.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Zatiaľ žiadne úpravy{vybrana ? " pre tohto dodávateľa" : ""}.
          </p>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dátum</TableHead>
                  <TableHead>Cenník</TableHead>
                  <TableHead>Akcia</TableHead>
                  <TableHead className="text-right">%</TableHead>
                  <TableHead className="text-right">Položiek</TableHead>
                  <TableHead>Kto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historia.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="font-mono text-xs">
                      {formatDatum(h.created_at)}
                    </TableCell>
                    <TableCell className="text-xs">{nazovSkupiny(h)}</TableCell>
                    <TableCell>
                      <AkciaBadge akcia={h.akcia} />
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {h.percent != null ? `${formatPct(h.percent)} %` : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {h.upravenych || "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {h.autor ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </section>
  );
}

/** Farebný badge typu udalosti v histórii úprav cien. */
function AkciaBadge({ akcia }: { akcia: string }): JSX.Element {
  if (akcia === "uprava") {
    return (
      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900 dark:text-amber-200">
        Úprava
      </span>
    );
  }
  if (akcia === "import_znovu") {
    return (
      <span
        className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
        title="Import cenníka uplatnil úpravu na ceny zo súboru — úprava platí ďalej"
      >
        Import, úprava ostala
      </span>
    );
  }
  if (akcia === "zrusenie") {
    return (
      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
        Zrušenie
      </span>
    );
  }
  return (
    <span
      className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-800 dark:bg-sky-900 dark:text-sky-200"
      title="Import cenníka prepísal ceny podľa súboru — úprava už nie je aplikovaná (do KOLA 165)"
    >
      Reset importom
    </span>
  );
}

/** „+3" / „−2,5" — percento so znamienkom pre badge úpravy cien. */
function formatPct(v: string | number): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  const s = (Math.round(n * 100) / 100).toLocaleString("sk-SK", {
    maximumFractionDigits: 2,
  });
  return n > 0 ? `+${s}` : s;
}
